import { gql } from '@apollo/client';
import {
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  HStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { Columns } from '~components/displays/DisplayTable/constants';
import {
  FilterContext,
  usePersistentFilters,
} from '~components/displays/DisplayTable/persistantFilters/persistantFilters';
import { FilterOptionPersisted } from '~components/displays/DisplayTable/persistantFilters/persistentFiltersModel';
import { WarningAlert } from '~components/ui/Alert';
import { BackButton } from '~components/ui/BackButton';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { DisplaysDetailStats } from '~components/ui/DisplaysDetailStats';
import { FileUploadButton } from '~components/ui/FileUploadButton';
import { MediaDurationIcon } from '~components/ui/icons/MediaDurationIcon';
import { PageHeading } from '~components/ui/PageHeading';
import { PlaylistType, PlaylistUpdateInput } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { formatSecondsToTimeString } from '~utils/durationConversion';
import { fromError } from '~utils/errors';
import { useFeatureFlag } from '~utils/features';
import { useSubscriptionType } from '~utils/subscriptions';
import { MaybePromise, OmitStrict } from '~utils/types';
import { useLimits } from '~utils/useLimits';
import { DeletePlaylistsModal } from '../DeletePlaylistsModal';
import { DurationModalType, HandleDurationModal } from '../DurationModals';
import { EditPlaylistModal } from '../EditPlaylistModal';
import {
  PlaylistMediaTable,
  usePlaylistMediaTable,
} from '../PlaylistMediaTable/PlaylistMediaTable';
import { SyncDisplaysModal } from '../SyncDisplaysModal';
import { useMediaList } from '../useMediaList';
import { useRemovePlaylistFromDisplays } from '../useRemovePlaylistFromDisplays';
import {
  PlaylistDetail_CustomerFragment,
  PlaylistDetail_PlaylistFragment,
  usePlaylistUpdateMutation,
} from './__generated__/PlaylistDetail.graphql';
import { PlaylistDetailMoreActions } from './PlaylistDetailMoreActions';

interface Props {
  customer: PlaylistDetail_CustomerFragment;
  playlist: PlaylistDetail_PlaylistFragment;
  onPlaylistUpdateSuccess: () => MaybePromise<void>;
}

export type FormValues = OmitStrict<PlaylistUpdateInput, 'mediaIds'>;

export function PlaylistDetail({ customer, playlist, onPlaylistUpdateSuccess }: Props) {
  const toast = useToast();
  const navigate = useNavigate();
  const syncDisplaysModal = useDisclosure();
  const deletePlaylistsModal = useDisclosure();
  const editPlaylistModal = useDisclosure();
  const setDurationModal = useDisclosure();
  const setDefaultDurationModal = useDisclosure();
  const { removePlaylistFromDisplays } = useRemovePlaylistFromDisplays();
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions, user } = useAuth();
  const { customer: customerHandle } = useParams();
  const { resetCustomerFilters, setColumnFilters } = usePersistentFilters(
    user?.subId ?? '',
    customerHandle ?? '',
    FilterContext.DISPLAYS,
    customer.id,
  );

  const hasPlaylistUpdatePermission = verifyUserPermissions([Permission.PlaylistUpdate]);

  const handleDeletePlaylistsSuccess = () => {
    deletePlaylistsModal.onClose();
    analytics.track('playlistDelete');
    navigate('../playlists');
  };

  const [updatePlaylist] = usePlaylistUpdateMutation();

  const { mediaList, reinitializeMediaList, updateMedia } = useMediaList({
    customerId: customer.id,
    initialMedia: playlist.media,
  });

  const { isEnabled: isPlaylistTypesEnabled } = useFeatureFlag('playlistTypes');
  const { hasWaveSubscription: hasEssentialSubscription } = useSubscriptionType(customer);
  const {
    playlists: { isPlaylistsLimitPerCustomerReached, isPlaylistMaximumFileLimitReached },
  } = useLimits();
  const isPlaylistsLimitReached = isPlaylistsLimitPerCustomerReached(
    customer.playlists.slice(1),
    hasEssentialSubscription,
  );

  const handleGoBack = () => {
    navigate('../playlists');
  };

  useEffect(() => {
    reinitializeMediaList(playlist.media);
  }, [playlist, reinitializeMediaList]);

  const handleRowMove = async (dragIndex: number, hoverIndex: number) => {
    const dragRecord = mediaList[dragIndex];
    const mediaWithoutDraggedItem = mediaList.filter((_, i) => i !== dragIndex);
    const newList = [
      ...mediaWithoutDraggedItem.slice(0, hoverIndex),
      dragRecord,
      ...mediaWithoutDraggedItem.slice(hoverIndex),
    ];
    const { data } = await updatePlaylist({
      variables: {
        input: {
          playlistId: playlist.id,
          mediaIds: newList.map((m) => m.id),
        },
      },
    });

    if (!data) {
      throw new Error('No data');
    }

    reinitializeMediaList(data.playlistUpdate.playlist.media);
  };

  const performBulkMediaRemoval = async (ids: string[]) => {
    try {
      const updatedList = mediaList.filter((m) => !ids.includes(m.id));

      const { data } = await updatePlaylist({
        variables: {
          input: {
            playlistId: playlist.id,
            title: playlist.title,
            description: playlist.description,
            mediaIds: updatedList.map((m) => m.id),
          },
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      analytics.track('playlistUpdate');

      reinitializeMediaList(data.playlistUpdate.playlist.media);
    } catch (err) {
      toast({
        status: 'error',
        title: t('editPlaylistErr'),
        description: fromError(err, 'EditPlaylist'),
      });
    }
  };

  const table = usePlaylistMediaTable(playlist, mediaList, {
    onRemoveMedia: performBulkMediaRemoval,
    isOverLiteLimit: isPlaylistsLimitReached,
  });

  const selectedMedia = table.selectedFlatRows.map((r) => r.original);

  const performMediaUpload = async (fileList: FileList) => {
    try {
      const newMediaList = await updateMedia(fileList);

      if (newMediaList.length === 0) {
        return;
      }

      const { data } = await updatePlaylist({
        variables: {
          input: {
            playlistId: playlist.id,
            mediaIds: newMediaList.map((m) => m.id),
          },
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      reinitializeMediaList(data.playlistUpdate.playlist.media);
      analytics.track('playlistUpdate');

      await onPlaylistUpdateSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: t('editPlaylistErr'),
        description: fromError(err, 'EditPlaylist', {
          MEDIA_NOT_FOUND: t('editPlaylistErrDesc'),
          INVALID_MIME_TYPE: t('editPlaylistErrDesc1'),
        }),
      });
    }
  };

  const handlePlaylistEdit = async () => {
    await onPlaylistUpdateSuccess();
    editPlaylistModal.onClose();
    analytics.track('playlistUpdate');
  };

  const removeDisplaysAction = useDestructiveAction<undefined>({
    title: t('removePlaylist'),
    message: t('removePlaylistMsg'),
    notice: t('removePlaylistNotice'),
    confirmLabel: t('remove'),
    onConfirm: async () => {
      await removePlaylistFromDisplays(playlist.id);
    },
  });

  const bulkDeleteMediaAction = useDestructiveAction<undefined>({
    title: t('deleteMedia'),
    message: t('deleteMediaMsg'),
    notice: t('deleteMediaNotice'),
    confirmLabel: t('deleteFiles'),
    onConfirm: async () => {
      await performBulkMediaRemoval(selectedMedia.map((m) => m.id));
    },
  });

  const canGoToLinkedDisplays = playlist.syncedDisplays.length > 0;
  const mediaLimitReached = isPlaylistMaximumFileLimitReached(playlist.media.length);
  const canUploadMedia = !mediaLimitReached && !isPlaylistsLimitReached;

  const handleGoToLinkedDisplays = async (
    playlistTitle: string,
    filters: Partial<Record<Columns, FilterOptionPersisted>> = {},
  ) => {
    await resetCustomerFilters();
    await setColumnFilters(Columns.Playlist, [{ id: playlist.id }]);

    for (const [key, value] of Object.entries(filters)) {
      await setColumnFilters(key, [value]);
    }

    navigate('../displays');
  };

  return (
    <>
      <PageHeading
        floatingButton={<BackButton onClick={handleGoBack} />}
        description={playlist.description || undefined}
        actions={
          <HStack>
            <FileUploadButton
              leftIcon={undefined}
              variant="solid"
              colorScheme="blue"
              onChange={performMediaUpload}
              isDisabled={!hasPlaylistUpdatePermission || !canUploadMedia}
              playlistType={playlist.playlistType}
            >
              {t('addMedia')}
            </FileUploadButton>
            <PlaylistDetailMoreActions
              playlist={playlist}
              isOverLiteLimit={isPlaylistsLimitReached}
              onRename={editPlaylistModal.onOpen}
              onSync={syncDisplaysModal.onOpen}
              onRemoveDisplays={() => removeDisplaysAction.askConfirmation(undefined)}
              onDelete={deletePlaylistsModal.onOpen}
            />
          </HStack>
        }
      >
        {playlist.title}
      </PageHeading>

      {isPlaylistsLimitReached && (
        <WarningAlert marginBottom="6">
          <AlertTitle>{t('playlistsLimitReached')}</AlertTitle>
          <AlertDescription>{t('playlistsLimitReachedAlert2')}</AlertDescription>
        </WarningAlert>
      )}

      {mediaLimitReached && (
        <WarningAlert>
          <AlertTitle>
            {t('reachedLimit')} {process.env.REACT_APP_PLAYLISTS_MAXIMUM_MEDIA_ENTRIES ?? ''}{' '}
            {t('reachedLimit1')}
          </AlertTitle>
        </WarningAlert>
      )}

      <HStack justifyContent="space-between" alignItems="flex-start" marginTop={'8'}>
        <DisplaysDetailStats
          syncedCount={playlist.syncedDisplays.length}
          outOfSyncCount={playlist.outOfSyncDisplays.length}
          syncingCount={playlist.syncingDisplays.length}
          removingCount={playlist.removingDisplays.length}
          onClickSyncedDisplays={
            canGoToLinkedDisplays ? () => handleGoToLinkedDisplays(playlist.title) : undefined
          }
          onClickOutOfSyncDisplays={() =>
            handleGoToLinkedDisplays(playlist.title, {
              [Columns.Warnings]: {
                id: 'isPlaylistOutOfSync',
              },
            })
          }
        />
        {!isEmpty(selectedMedia) ? (
          <Button
            variant={'solid'}
            colorScheme={'red'}
            onClick={() => bulkDeleteMediaAction.askConfirmation(undefined)}
            isDisabled={!hasPlaylistUpdatePermission || isPlaylistsLimitReached}
          >
            {t('deleteSelected')}
          </Button>
        ) : undefined}
      </HStack>
      {playlist.playlistType === PlaylistType.Image && isPlaylistTypesEnabled && (
        <HStack my={6} spacing={8}>
          <Button
            variant="outline"
            size="sm"
            isDisabled={isEmpty(selectedMedia)}
            onClick={setDurationModal.onOpen}
          >
            {t('setduration')}
          </Button>

          <Button variant="unstyled" color="#1D85E8" onClick={setDefaultDurationModal.onOpen}>
            <HStack spacing={3}>
              <MediaDurationIcon boxSize="8" isEnabled />
              <span>
                {t('defaultDuration')} : {formatSecondsToTimeString(playlist.defaultDuration)}
              </span>
            </HStack>
          </Button>
        </HStack>
      )}

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        background="white"
        marginTop={2}
      >
        <PlaylistMediaTable
          playlist={playlist}
          table={table}
          onRowMove={handleRowMove}
          onFileUpload={performMediaUpload}
          isOverLiteLimit={isPlaylistsLimitReached}
        />
      </Box>

      {removeDisplaysAction.confirmationNode}
      {bulkDeleteMediaAction.confirmationNode}
      <SyncDisplaysModal
        isOpen={syncDisplaysModal.isOpen}
        playlist={playlist}
        onSuccess={syncDisplaysModal.onClose}
        onCancel={syncDisplaysModal.onClose}
      />
      <DeletePlaylistsModal
        isOpen={deletePlaylistsModal.isOpen}
        playlists={[playlist]}
        onCancel={deletePlaylistsModal.onClose}
        onSuccess={handleDeletePlaylistsSuccess}
      />
      <EditPlaylistModal
        isOpen={editPlaylistModal.isOpen}
        playlist={playlist}
        onCancel={editPlaylistModal.onClose}
        onSuccess={handlePlaylistEdit}
      />
      {playlist.playlistType === PlaylistType.Image && (
        <>
          <HandleDurationModal
            isOpen={setDurationModal.isOpen}
            playlist={playlist}
            onCancel={setDurationModal.onClose}
            selectedMedia={selectedMedia}
            variant={DurationModalType.MediaDuration}
          />
          <HandleDurationModal
            isOpen={setDefaultDurationModal.isOpen}
            onCancel={setDefaultDurationModal.onClose}
            playlist={playlist}
            selectedMedia={[]}
            variant={DurationModalType.PlaylistDefaultDuration}
          />
        </>
      )}
    </>
  );
}

PlaylistDetail.graphql = {
  fragments: {
    PlaylistDetail_customer: gql`
      fragment PlaylistDetail_customer on Customer {
        id
        name
        displayCount
        waveSubscriptionUsage
        waveSubscription {
          id
          usage {
            current
            max
          }
        }
        waveSubscriptions {
          id
          validUntil
          usage {
            current
            max
          }
          status
        }
        playlists {
          id
        }
      }
    `,
    PlaylistDetail_playlist: gql`
      fragment PlaylistDetail_playlist on Playlist {
        id
        title
        description
        playlistType
        defaultDuration
        isPlaylistUsedByAnyConfiguration
        isPlaylistUsedByAnySchedule
        media {
          ...PlaylistMediaTable_media
        }
        syncedDisplays: displays(filter: { state: SYNCED }) {
          id
        }
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
        syncingDisplays: displays(filter: { state: SYNCING }) {
          id
        }
        removingDisplays: displays(filter: { state: REMOVING }) {
          id
        }
        ...SyncDisplaysModal_playlist
      }
    `,
  },
  mutations: {
    PlaylistUpdate: gql`
      mutation PlaylistUpdate($input: PlaylistUpdateInput!) {
        playlistUpdate(input: $input) {
          playlist {
            id
            ...PlaylistDetail_playlist
          }
        }
      }
    `,
  },
};
